/**
 * CiscoStatusCallout — a reusable banner reminding operators to check Cisco's
 * Global Service Status BEFORE diving into WxC / WxCC troubleshooting.
 *
 * Mount this on every page where someone might investigate a Webex problem:
 *   - Status.tsx          (platform-wide status)
 *   - Spokes.tsx          (per-tenant spoke diagnostics)
 *   - Support.tsx         (above the "open ticket" form)
 *   - Any WxC/WxCC drilldown the dashboard later acquires
 *
 * Phase 2 (later): replace the static link with a live banner that pulls
 * the current incident summary from cisco-status-svc and shows region-aware
 * details ("Cisco WxCC NA-West: Degraded since 09:58 PT").
 */

interface Props {
  /** Optional override of the default text. Useful when the surrounding page
   *  is specifically about WxC vs WxCC (e.g. only show "WxC" copy). */
  scope?: 'wxc' | 'wxcc' | 'both';
  /** Compact mode — smaller padding for use inside cards. Default false. */
  compact?: boolean;
}

const CISCO_STATUS_URL = 'https://status.webex.com/commercial/status?lang=en_US';

export default function CiscoStatusCallout({ scope = 'both', compact = false }: Props) {
  const scopeLabel =
    scope === 'wxc' ? 'Webex Calling (WxC)' :
    scope === 'wxcc' ? 'Webex Contact Center (WxCC)' :
    'Webex Calling (WxC) or Webex Contact Center (WxCC)';

  return (
    <div
      className={`card border-l-4 border-tnh-warn bg-yellow-50/60 dark:bg-yellow-900/15 ${
        compact ? 'p-3 mb-4' : 'p-4 mb-6'
      }`}
      role="note"
      aria-label="Cisco global service status reminder"
    >
      <div className="flex items-start gap-3">
        <span className="text-tnh-warn text-lg leading-none mt-0.5" aria-hidden>⚠</span>
        <div className="flex-1">
          <div className="font-semibold text-sm">
            Check Cisco Global Service Status first
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
            Before troubleshooting {scopeLabel} issues, verify there is no
            active Cisco-side incident affecting your region. Many "outages"
            traced through TriNetHub turn out to be Cisco-side degradations
            visible only on the public status feed.
          </p>
          <a
            href={CISCO_STATUS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-tnh-accent hover:underline text-sm mt-2 font-medium"
          >
            Open status.webex.com →
          </a>
        </div>
      </div>
    </div>
  );
}

export { CISCO_STATUS_URL };
